import { apiClient } from './client';
import { METIER_BACKEND_CODE } from './types';
import type {
  ArbitrerPieceInput,
  CreatePersonnelInput,
  CreatePersonnelPayload,
  Dossier,
  DossierPieceField,
  ListPersonnelParams,
  Paginated,
  Personnel,
  PersonnelListItem,
} from './types';

/**
 * Endpoints `/api/v1/personnel/` — Phase 2.
 *
 * Implémente la couche réseau pour la console Admin. Toutes les fonctions sont
 * des wrappers axios typés ; le cache et la déduplication sont gérés côté
 * TanStack Query au point d'appel.
 */

function cleanParams(params: ListPersonnelParams): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  if (params.page !== undefined) out.page = params.page;
  if (params.page_size !== undefined) out.page_size = params.page_size;
  if (params.statut !== undefined) out.statut = params.statut;
  if (params.metier !== undefined) out.metier = params.metier;
  if (params.search !== undefined && params.search.length > 0) out.search = params.search;
  return out;
}

export async function listPersonnel(
  params: ListPersonnelParams = {},
): Promise<Paginated<PersonnelListItem>> {
  const { data } = await apiClient.get<Paginated<PersonnelListItem>>('/personnel/', {
    params: cleanParams(params),
  });
  return data;
}

export async function getPersonnel(id: string): Promise<Personnel> {
  const { data } = await apiClient.get<Personnel>(`/personnel/${id}/`);
  return data;
}

/**
 * Met à jour partiellement un personnel (PATCH /personnel/{id}/), réservé au
 * staff. Utilisé p.ex. pour renseigner le lieu de résidence affiché sur les
 * contrats.
 */
export async function updatePersonnel(
  id: string,
  patch: Partial<Pick<Personnel, 'residence' | 'secteur' | 'notes'>>,
): Promise<Personnel> {
  const { data } = await apiClient.patch<Personnel>(`/personnel/${id}/`, patch);
  return data;
}

/**
 * Construit la charge utile exacte attendue par le serializer DRF à partir des
 * valeurs du formulaire : le code métier est traduit vers l'énumération Django,
 * `telephone` devient `phone`, et seuls les champs acceptés à la création sont
 * transmis (le garant est saisi ultérieurement via le flux dossier).
 */
export function toCreatePersonnelPayload(input: CreatePersonnelInput): CreatePersonnelPayload {
  const payload: CreatePersonnelPayload = {
    metier: METIER_BACKEND_CODE[input.metier],
    first_name: input.first_name.trim(),
    last_name: input.last_name.trim(),
    phone: input.telephone.trim(),
  };
  // E-mail optionnel : si absent, le backend en synthétise un depuis le téléphone.
  const email = input.email?.trim();
  if (email !== undefined && email.length > 0) {
    payload.email = email;
  }
  const secteur = input.secteur?.trim();
  if (secteur !== undefined && secteur.length > 0) {
    payload.secteur = secteur;
  }
  const residence = input.residence?.trim();
  if (residence !== undefined && residence.length > 0) {
    payload.residence = residence;
  }
  return payload;
}

export async function createPersonnel(input: CreatePersonnelInput): Promise<Personnel> {
  const { data } = await apiClient.post<Personnel>('/personnel/', toCreatePersonnelPayload(input));
  return data;
}

/**
 * Dossier d'onboarding — récupéré séparément du personnel (serializers distincts).
 * Le backend crée le dossier à la volée (`get_or_create`) : un 404 ne survient
 * que si le personnel lui-même est introuvable.
 */
export async function getDossier(id: string): Promise<Dossier> {
  const { data } = await apiClient.get<Dossier>(`/personnel/${id}/dossier/`);
  return data;
}

/**
 * Définit/réinitialise le mot de passe du personnel (l'agence le communique
 * ensuite à l'intéressé). Le personnel se connecte par matricule + mot de passe
 * — il n'y a plus d'OTP SMS.
 */
export async function definirMotDePassePersonnel(
  id: string,
  password: string,
): Promise<void> {
  await apiClient.post(`/personnel/${id}/definir-mot-de-passe/`, { password });
}

export async function validerDossier(id: string, commentaire?: string): Promise<Dossier> {
  const payload = commentaire !== undefined ? { commentaire } : {};
  const { data } = await apiClient.post<Dossier>(`/personnel/${id}/dossier/valider`, payload);
  return data;
}

export async function rejeterDossier(id: string, commentaire: string): Promise<Dossier> {
  const { data } = await apiClient.post<Dossier>(`/personnel/${id}/dossier/rejeter`, {
    commentaire,
  });
  return data;
}

export async function relancerDossier(id: string): Promise<Dossier> {
  const { data } = await apiClient.post<Dossier>(`/personnel/${id}/dossier/relancer`);
  return data;
}

/**
 * Arbitrage d'une pièce individuelle du dossier.
 * `POST /api/v1/personnel/{id}/dossier/piece/`
 * Renvoie le dossier mis à jour (avec `pieces_statut` actualisé).
 */
export async function arbitrerPiece(id: string, input: ArbitrerPieceInput): Promise<Dossier> {
  const payload: ArbitrerPieceInput = { piece: input.piece, statut: input.statut };
  if (input.commentaire !== undefined && input.commentaire.length > 0) {
    payload.commentaire = input.commentaire;
  }
  const { data } = await apiClient.post<Dossier>(`/personnel/${id}/dossier/piece/`, payload);
  return data;
}

/**
 * Upload multipart pour une photo (portrait ou pleine) ou une pièce.
 * Le serveur scanne avec ClamAV (Phase 2.3) avant d'accepter.
 */
export async function uploadPhoto(
  id: string,
  type: 'PORTRAIT' | 'PLEINE',
  file: File,
): Promise<Personnel> {
  const form = new FormData();
  form.append('type', type);
  form.append('file', file);
  const { data } = await apiClient.post<Personnel>(`/personnel/${id}/photos/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ── Uploads dossier (Admin / RH) ───────────────────────────────────────────

/**
 * Téléverse une pièce du dossier d'onboarding au nom d'un personnel.
 * Accessible aux rôles ADMIN et RH (IsRHOrOwnerPersonnel côté serveur).
 * Timeout élevé car les fichiers peuvent être volumineux (scan ClamAV inclus).
 *
 * `POST /personnel/{id}/dossier/upload/`
 * @returns Le dossier mis à jour avec les URLs des pièces.
 */
export async function uploadDossierPiece(
  personnelId: string,
  field: DossierPieceField,
  file: File,
): Promise<Dossier> {
  const form = new FormData();
  form.append('field', field);
  form.append('file', file);
  const { data } = await apiClient.post<Dossier>(
    `/personnel/${personnelId}/dossier/upload/`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    },
  );
  return data;
}

/**
 * Met à jour la localisation du domicile d'un personnel.
 *
 * `POST /personnel/{id}/dossier/domicile/`
 */
export async function updateDossierDomicile(
  personnelId: string,
  coords: { latitude: number; longitude: number },
): Promise<Dossier> {
  const { data } = await apiClient.post<Dossier>(
    `/personnel/${personnelId}/dossier/domicile/`,
    coords,
  );
  return data;
}

/** Champs de saisie d'un garant (POST/PATCH /personnel/{id}/dossier/garant/). */
export interface SaveGarantInput {
  nom: string;
  prenom: string;
  telephone: string;
  lien_parente?: string;
  adresse?: string;
  /** Numéro CNI du garant (chiffré au repos côté serveur). */
  cni_garant?: string;
}

/**
 * Crée ou met à jour LE garant du dossier (nom, prénom, tél, lien, CNI, adresse).
 * La pièce d'identité s'envoie ensuite via {@link uploadGarantPiece}.
 *
 * `POST /personnel/{id}/dossier/garant/`
 */
export async function saveGarant(
  personnelId: string,
  input: SaveGarantInput,
): Promise<Dossier> {
  const payload: Record<string, string> = {
    nom: input.nom.trim(),
    prenom: input.prenom.trim(),
    telephone: input.telephone.trim(),
  };
  if (input.lien_parente?.trim()) payload.lien_parente = input.lien_parente.trim();
  if (input.adresse?.trim()) payload.adresse = input.adresse.trim();
  if (input.cni_garant?.trim()) payload.cni_garant = input.cni_garant.trim();
  const { data } = await apiClient.post<Dossier>(
    `/personnel/${personnelId}/dossier/garant/`,
    payload,
  );
  return data;
}

/**
 * Téléverse la pièce d'identité du garant.
 * Renvoie 400 si aucun garant n'est enregistré pour ce personnel.
 *
 * `POST /personnel/{id}/dossier/garant-piece/`
 */
export async function uploadGarantPiece(
  personnelId: string,
  file: File,
): Promise<Dossier> {
  const form = new FormData();
  form.append('file', file);
  const { data } = await apiClient.post<Dossier>(
    `/personnel/${personnelId}/dossier/garant-piece/`,
    form,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
    },
  );
  return data;
}
